export interface HeaderMenuItem {
  id: string
  label: string
  icon: string
  action: () => void
}

export interface FabAction {
  icon: string
  /** Accessible name — the FAB is icon-only. */
  label: string
  action: () => void
}

export interface HeaderAction {
  icon: string
  action: () => void
  title?: string
  spinning?: boolean
}

export interface HeaderBackAction {
  action: () => void
}

export const useHeaderMenu = () => {
  const items = useState<HeaderMenuItem[]>('headerMenuItems', () => [])
  const fab = useState<FabAction | null>('headerFab', () => null)
  const headerAction = useState<HeaderAction | null>('headerAction', () => null)
  const backAction = useState<HeaderBackAction | null>('headerBackAction', () => null)
  // Pages with their own mobile header (e.g. tender detail) hide the layout's generic
  // one instead of showing both stacked.
  const hideMobileHeader = useState<boolean>('headerHideMobile', () => false)
  // Pages that manage their own internal scroll regions on desktop (nav strips pinned via
  // shrink-0 siblings rather than position:sticky, e.g. calendar.vue) need the layout's own
  // <main> to NOT also be scrollable there — two competing scroll containers means any content
  // that ever exceeds <main>'s own box (even by a px) makes <main> itself start scrolling,
  // carrying away everything that was only "pinned" by living outside the page's own inner
  // overflow-y-auto region (only truly position:sticky elements, like the page's own header,
  // stay put in that case) instead of just the page's own intended scroll area. Only applies at
  // the lg breakpoint (see default.vue's <main> binding) — on mobile calendar.vue drops its own
  // height constraint instead, so <main> stays the one and only scroll container there and
  // everything below the sticky mobile header (nav strip, weekday header, day grid) scrolls
  // together as one normal page, not as separately pinned regions.
  const noMainScroll = useState<boolean>('headerNoMainScroll', () => false)

  const setItems = (newItems: HeaderMenuItem[]) => {
    items.value = newItems
  }

  const clearItems = () => {
    items.value = []
  }

  const setFab = (action: FabAction) => {
    fab.value = action
  }

  const clearFab = () => {
    fab.value = null
  }

  const setHeaderAction = (action: HeaderAction) => {
    headerAction.value = action
  }

  const clearHeaderAction = () => {
    headerAction.value = null
  }

  const setBackAction = (action: HeaderBackAction) => {
    backAction.value = action
  }

  const clearBackAction = () => {
    backAction.value = null
  }

  const setHideMobileHeader = (value: boolean) => {
    hideMobileHeader.value = value
  }

  const setNoMainScroll = (value: boolean) => {
    noMainScroll.value = value
  }

  return { items, setItems, clearItems, fab, setFab, clearFab, headerAction, setHeaderAction, clearHeaderAction, backAction, setBackAction, clearBackAction, hideMobileHeader, setHideMobileHeader, noMainScroll, setNoMainScroll }
}
